import React from "react";
import { RCCBadge, RCCButton, RCCGrid, RCCIcon, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { ActionEfficiency, ActionSituationFilter } from "src/pages/Actions/Panel/interface";
import formatDate from "src/utils/formatDate";
import Card from "src/components/Card";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import {formatMinutesInHours} from "src/utils/formartHourMinutes";
import { ICardActions } from "./interface";
import {  DivIcons, DivPostponement, DivWithWrap, StyledIcon, StyledSpan } from "./styles";
import BadgeTraceabilityCard from "../BadgeTraceability/badgeTraceabilityCard";

const CardActions: React.FC<ICardActions> = ({
	data,
	options,
	openModalComments,
	openModalSubactions,
	openModalAttachments,
	openModalPostPonementConfirm,
	openModalCancelConfirm,
	openModalEfficiency,
	openModalChangeProgress,
	openModalShowPostponements,
	layout
}) => {
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const { translate, currentLanguage } = useTranslation();

	let {daysCount} = data;
	let color: "grey" | "green" | "blue" | "red" | "yellow" | "purple" = "grey";
	switch(data.situation){
	case ActionSituationFilter.FINISHED:
		daysCount = 0;
		color = "green";
		break;
	case ActionSituationFilter.DELAYED:
		color = "red";
		break;
	case ActionSituationFilter.NOT_STARTED:
		daysCount = 0;
		color = "purple";
		break;
	case ActionSituationFilter.IN_PROGRESS:
		daysCount = 0;
		color = "blue";
		break;
	case ActionSituationFilter.NEXT_TO_EXPIRE:
		color = "yellow";
		break;
	default: 
		daysCount = 0;
		break;
	}

	let percentageColor = "";
	switch(data.percentageColorConcluded){
	case "red":
		percentageColor = "var(--red)";
		break;
	case "yellow":
		percentageColor = "var(--yellow)";
		break;
	default: 
		percentageColor = "var(--purple)";
		break;
	}

	return (
		<Card
			color={color}
			options={options(data)}
			percentage={data.inb001PercConclusao}
			percentageColor={percentageColor}
			percentagePredicted={data.percentagePredicted}
			title={`${data.inb001Codigo} - ${data.inb001Titulo}`}
			onClickPercentage={() => openModalAttachments(data, false)}
			widthProgress="54px"
			layout="withProgressBarPredicted"
		>		 
			<RCCGrid container item alignItems="center" >
				<DivWithWrap style={{marginRight: "0.5rem"}}>
					<Photo 
						name={data.inb001Efa005Nome}
						link={data.inb001Efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${data.inb001Efa005Imagem}`}
						hover
					/>
					{data.inb001Efa005Nome}
				</DivWithWrap>
				{layout !== "simplified" && (
					<DivWithWrap style={data.inb003Dtfim && data.inb003Dtfim !== null ? { flexDirection: "column", alignItems: "baseline" } : {}}>
						{data.inb003Dtfim && data.inb003Dtfim !== null && (
							<DivPostponement onClick={() => openModalShowPostponements(data)}>
								<RCCIcon name="calendar" size={16} color="var(--red)" />
								{formatDate(data.inb003Dtfim, currentLanguage)}
							</DivPostponement>
						)}
						<DivWithWrap>
							<RCCIcon name="calendar" size={16} color="var(--default-color-font)" />
							{formatDate(data.inb001Dtinicio, currentLanguage)} - {formatDate(data.inb001Dtfim, currentLanguage)}
						</DivWithWrap>
					</DivWithWrap>
				)}
			</RCCGrid>
			<RCCGrid
				container
				item
				alignItems="center"
				justifyContent="space-between"
			>
				<DivWithWrap>
					{data.isTask && (
						<DivWithWrap>
							<RCCGrid container item alignItems="center">
								<RCCIcon 
									name="clock"
									color="var(--default-color-font)"
									size={16}
									style={{marginRight: "4px"}}
								/>

								<Title color="purple" marginBottom={0} marginTop={0} styleInline="font-size: 0.85rem;">
									{formatMinutesInHours(data.taskDurationRealized)}
								</Title>
								/
								<Title color="defaultColor" marginBottom={0} marginTop={0} styleInline="font-size: 0.85rem;">
									{formatMinutesInHours(data.taskDuration)}
								</Title>

								{data.situation !== ActionSituationFilter.FINISHED && data.situation !== ActionSituationFilter.CANCELED &&  data.situation !== ActionSituationFilter.DRAFT && (
									<>
										{data.inb004Id && data.inb004Id > 0 && !data.inb004DataFinal ? (
											<RCCButton 
												color="grey"
												onClick={() => openModalChangeProgress(data)} 
												styleInline="padding: 2px 8px; border-radius: 10px; font-size: 0.78rem;"
											>
												<RCCGrid
													container
													item
													alignItems="center"
												>
													<RCCIcon name="pause" style={{ marginRight: "5px" }} size={10} />
													{translate("Pausar")}
												</RCCGrid>
											</RCCButton>
										) : (
											<RCCButton 
												color="grey"
												onClick={() => openModalChangeProgress(data)} 
												styleInline="padding: 2px 8px; border-radius: 10px; font-size: 0.78rem;"
											>
												<RCCGrid
													container
													item
													alignItems="center"
												>
													<RCCIcon name="play"  />
													{translate("Iniciar")}
												</RCCGrid>
											</RCCButton>
										)}
									</>
								)}
							</RCCGrid>
						</DivWithWrap>
					)}
					{data.situation !== ActionSituationFilter.FINISHED && data.situation !== ActionSituationFilter.CANCELED && 
					 data.situation !== ActionSituationFilter.DRAFT ? (
							<RCCButton color={color} onClick={() => openModalChangeProgress(data)} styleInline="padding: 2px 8px; border-radius: 10px; font-size: 0.78rem;">{translate(data.situation)}{daysCount === 0 ? "" : `: ${Math.abs(daysCount)} ${translate("dia(s)")}`}</RCCButton>
					 ): (
							<RCCBadge color={color} styleInline="padding: 2px 8px; font-size: 0.78rem;">{translate(data.situation)}{daysCount === 0 ? "" : `: ${Math.abs(daysCount)} ${translate("dia(s)")}`}</RCCBadge>
					 )}
					<BadgeTraceabilityCard 
						id={data.inb001Id}
						codTraceability={data.traceability}
					/>
					{data.isSubaction && (
						<RCCBadge color="grey" styleInline="padding: 2px 8px; font-size: 0.78rem;" >{translate("Subação")}</RCCBadge>
					)}
					{data.efficiency !== ActionEfficiency.NOT_NEEDED && (
						<>
							{data.efficiency === ActionEfficiency.NEEDED ? (
								<RCCBadge color="yellow" styleInline="padding: 2px 8px; font-size: 0.78rem; cursor: pointer;" onClick={() => openModalEfficiency(data)}>{translate("Requer eficácia")}</RCCBadge>
							): (
								<RCCBadge color="red" styleInline="padding: 2px 8px; font-size: 0.78rem; cursor: pointer;" onClick={() => openModalEfficiency(data)}>{translate("Eficácia atrasada")}</RCCBadge>
							)}
						</>
					)}
					{data.situation === ActionSituationFilter.FINISHED && data.efficiency === ActionEfficiency.NOT_NEEDED && data.inb001Exigeeficacia === "S" && (
						<DivIcons onClick={() => openModalEfficiency(data)}>
							<RCCIcon name="listCheck" color="var(--default-color-font)" size={20}/>
						</DivIcons>
					)}
					{data.situation !== ActionSituationFilter.FINISHED && data.situation !== ActionSituationFilter.CANCELED && 
					 data.situation !== ActionSituationFilter.DRAFT && (
						 <>
						 {data.hasPostponement && (
								<RCCTooltip
									color="yellow"
									text={translate("Alerta postergação")}
								>
									<DivIcons onClick={() => openModalPostPonementConfirm(data)}>
										<RCCIcon name="exclamationTriangle" color="var(--yellow)" size={14}/>
									</DivIcons>
								</RCCTooltip>
							)}
							{data.hasCancel && (
								<RCCTooltip
									color="red"
									text={translate("Alerta cancelamento")}
								>
									<DivIcons onClick={() => openModalCancelConfirm(data)}>
										<RCCIcon name="exclamationTriangle" color="var(--red)" size={14}/>
									</DivIcons>
								</RCCTooltip>
							)}
						 </>
					)}

					{layout !== "simplified" && (
						<>
							<DivIcons onClick={() => openModalComments(data)}>
								{data.commentsNumber > 0 ? (
									<>
										<RCCIcon name="commentFilled" color="var(--default-color-font)" size={12}/>
										{data.commentsNumber}
									</>
								) : (
									<RCCIcon name="comment" color="var(--default-color-font)" size={12}/>
								)}
							</DivIcons>
							<DivIcons onClick={() => openModalAttachments(data, true)}>
								<RCCIcon name="attachment" color="var(--default-color-font)" size={12}/>
								{data.attachmentsNumber > 0 && data.attachmentsNumber}
							</DivIcons>
						</>
					)}


				</DivWithWrap>
				{data.subactionsNumber > 0 && (
					<div
						style={{ display: "flex", alignItems: "center" }}
					>
						<StyledIcon 
							name="tree"
							size={18}
							onClick={() => openModalSubactions(data)}
						/>
						<StyledSpan>
							{data.subactionsNumber}
						</StyledSpan>
					</div>
				)}
			</RCCGrid>
		</Card>
	);
};

export default CardActions;
